/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { cssMap, jsx } from '@compiled/react';

import type { StrictXCSSProp } from '@atlaskit/css';

// These are "compressed" to discourage direct usage.
// Using `opacity` properties so that we can animate the fade in/out.
const opacityCssVar = '--n_sfc_o';
// `display` is used when animations are not needed - when `fadeAnimation` is false.
const displayCssVar = '--n_sfc_d';

const surfaceStyles = cssMap({
	root: {
		[opacityCssVar]: '0%',
		[displayCssVar]: 'none',
		'&:hover, &:focus-within': {
			[opacityCssVar]: '100%', // Using percentages instead of number as Compiled will convert `1` to `1px`.
			[displayCssVar]: 'block',
		},
	},
});

/**
 * __InteractionSurface__
 *
 * The parent interaction surface that applies styles to its `InteractionConsumer` children
 * when interacted with (focus, hover).
 */
export const InteractionSurface = ({
	children,
	xcss,
}: {
	children: React.ReactNode;
	xcss?: StrictXCSSProp<'width' | 'height', never>;
}) => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
	<div css={surfaceStyles.root} className={xcss}>
		{children}
	</div>
);

const interactionConsumerStyles = cssMap({
	shownOnInteractionWithFade: {
		// Opacity is used to animate the fade in/out.
		opacity: `var(${opacityCssVar})`,
		transition: 'opacity .2s',
	},
	shownOnInteractionWithoutFade: {
		// We can simply use the `display` property, as we don't have to worry about animations.
		display: `var(${displayCssVar})`,
	},
});

/**
 * InteractionConsumer
 *
 * The child of an `InteractionSurface` that is styled based on interactions made to the surface.
 *
 * It can optionally fade in/out, using the `fadeAnimation` prop. Note: if this is enabled, the element
 * will still be in the document flow and will take up space.
 */
export const InteractionConsumer = ({
	children,
	testId,
	xcss,
	fadeAnimation = false,
	type = 'shownOnInteraction',
}: {
	children: React.ReactNode;
	testId?: string;
	xcss?: StrictXCSSProp<'backgroundColor' | 'gridRow' | 'gridColumn', never>;
	/**
	 * Whether to apply a fade in/out animation. Defaults to false.
	 *
	 * If this is enabled, the element will still be in the document flow - meaning it will take up space.
	 */
	fadeAnimation?: boolean;
	/**
	 * The behaviour of the element.
	 * - `showOnInteraction`: The element will only be shown when the parent `InteractionSurface` is interacted with (hovered or focused).
	 * - `alwaysShown`: The element will always be shown.
	 */
	type?: 'shownOnInteraction' | 'alwaysShown';
}) => (
	<div
		css={[
			fadeAnimation &&
				type === 'shownOnInteraction' &&
				interactionConsumerStyles.shownOnInteractionWithFade,
			!fadeAnimation &&
				type === 'shownOnInteraction' &&
				interactionConsumerStyles.shownOnInteractionWithoutFade,
		]}
		data-testid={testId}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
		className={xcss}
	>
		{children}
	</div>
);
